import {TextPrinter} from "../../global/htmlprinter";
import {BoxRelativeToPart} from "../owayoPartWithSelObs";
import {autocurry, compose, rightCurry} from "../../global/functionalTools";
import {CONSTANTS} from "../meta_data";
import {Vector2} from "../delme_globals";

export class ShaderPrinter extends TextPrinter {
    public static endline = ";\n";
    public static funcWriter = autocurry(
        (funcname, firstbodyline, ...restbody) => ShaderPrinter.prepend(funcname, ShaderPrinter.roundbracket(ShaderPrinter.joinarguments(firstbodyline, restbody, ", ")))
    );
    public static nthArrayEntry = autocurry((n, arrayname) => ShaderPrinter.append(ShaderPrinter.squarebracket(n), arrayname));

    public static bracketArgs = (...args) => args.map(x => ShaderPrinter.roundbracket(x));
    public static prop = ShaderPrinter.brace(".");
    public static add = ShaderPrinter.join(" + ");
    public static subtract = ShaderPrinter.join(" - ");
    public static divide = compose(ShaderPrinter.join(" / "), ShaderPrinter.bracketArgs);
    public static times = compose(ShaderPrinter.join(" * "), ShaderPrinter.bracketArgs);

    public static squarebracket = rightCurry(ShaderPrinter.brace, "[", "]");
    public static define = compose(rightCurry(ShaderPrinter.brace, ShaderPrinter.endline), ShaderPrinter.join(" "));
    public static uniform = compose(ShaderPrinter.prepend("uniform "), ShaderPrinter.define);
    public static varying = compose(ShaderPrinter.brace, "varying ", ShaderPrinter.endline);

    public static or = ShaderPrinter.join(" || ");
    public static and = ShaderPrinter.join(" && ");
    public static for = (lowerbound, upperbound)=>loopcontent=>{
        return "for(int i="+lowerbound+"; i < "+upperbound+"; i++){"+loopcontent+"}"+ShaderPrinter.endline;
    }
    public static assign = compose(ShaderPrinter.append(";\n"), ShaderPrinter.brace(" = "));

    public static compare = (a, b, operand) => ShaderPrinter.brace(operand, a, b);
    public static lessThan = ShaderPrinter.brace(" < ");
    public static lessOrEqualThan = ShaderPrinter.brace(" <= ");
    public static greaterThan = ShaderPrinter.brace(" > ");
    public static greaterOrEqualThan = ShaderPrinter.brace(" >= ");
    public static equal = ShaderPrinter.brace(" == ");
    public static notEqual = ShaderPrinter.brace(" != ");
    public static not = ShaderPrinter.prepend("!");
    public static allComponentsLessThan = (variableName, components, limit)=> ShaderPrinter.and(
        components.map(component => ShaderPrinter.lessThan(ShaderPrinter.prop(variableName, component), limit))
    );
    public static if = autocurry(
        function (condition: string, firstline, ...body: string[]) {
            return ShaderPrinter.brace(
                ShaderPrinter.roundbracket(condition),
                "if ",
                ShaderPrinter.brace(ShaderPrinter.joinarguments(firstline, body), " {\n", "}\n")
            );
        }
    );

    public static else = compose(rightCurry(ShaderPrinter.brace, " else {\n", "\n}\n"), (...x) => x.join("\n"));

    public static x = rightCurry(ShaderPrinter.prop, "x");
    public static y = rightCurry(ShaderPrinter.prop, "y");
    public static vec2 = ShaderPrinter.funcWriter("vec2");
    public static vec3 = ShaderPrinter.funcWriter("vec3");
    public static abs = ShaderPrinter.funcWriter("abs");
    public static texture2D = ShaderPrinter.funcWriter("texture2D");
    public static mod = ShaderPrinter.funcWriter("mod");
    public static clamp = ShaderPrinter.funcWriter("clamp");
    public static defineVar = autocurry((type, name, value)=>ShaderPrinter.join(" ", type, ShaderPrinter.assign(name, value)));
    public static defineBoolvar = ShaderPrinter.defineVar("bool");
    public static defineVec3var = ShaderPrinter.defineVar("vec3");
    protected static getDimFunction = dimension => dimension === "x" ? ShaderPrinter.x : ShaderPrinter.y;
}


export class OwayoShaderDruckbereichPrinter {

    public static isInDruckbereich(druckBereich: BoxRelativeToPart | null, nichtDruckbereich:BoxRelativeToPart|null) {
        const p = ShaderPrinter;
        if(druckBereich === null) {
            return "true";
        }
        const shadervec2 = (v:Vector2)=>p.vec2(v.x.toFixed(4), v.y.toFixed(4));
        const isInBox = p.funcWriter("isInBox");
        const inDruckBereich = isInBox("vOriginalUv", shadervec2(druckBereich.topRight), shadervec2(druckBereich.bottomLeft));
        if (nichtDruckbereich === null) {
            return inDruckBereich;
        }
        const nichtInNichtDruckBereich = p.not(isInBox("vOriginalUv", shadervec2(nichtDruckbereich.topRight), shadervec2(nichtDruckbereich.bottomLeft)))
        return p.and(inDruckBereich, nichtInNichtDruckBereich)

    }

    public static greyOutNotPrintableAreaIfFlagIsSet() {
        return ' ColorResult = vec4( mix( ColorResult.rgb, vec3( 0.2,0.2,0.2 ), 1.0 * float( isGreyedOutExceptDruckbereich ) ), ColorResult.a);';
    }
}

class LogoRepeatColorGetter extends ShaderPrinter {
    public static getLogoRepeatColorResult(nthIndex: (arrayname) => string): string {
        const p = ShaderPrinter;

        function getcoordinate(dimension) {
            const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
            return p.add(
                p.mod(
                    p.subtract(dim("posInSelOb"), dim(nthIndex("selObMargins"))),
                    p.subtract(
                        dim(nthIndex("SelectableObjectTextureSize")),
                        p.times("2.0", dim(nthIndex("selObMargins")))
                    )
                ),
                dim(nthIndex("selObMargins"))
            );
        }

        function relCoordinate(coord, dimension): string {
            const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
            return p.divide(coord, dim(nthIndex("SelectableObjectTextureSize")));
        }

        function clampToMargins(val, dimension): string {
            const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
            const minMargin = ShaderPrinter.roundbracket(ShaderPrinter.add(dim(nthIndex("selObMargins")), "2.0"));
            const maxMargin = p.subtract(dim(nthIndex("SelectableObjectTextureSize")), minMargin);
            return p.clamp(val, minMargin, maxMargin);
        }

        return p.texture2D(
            nthIndex('SelectableObjectTexture'),
            p.vec2(
                relCoordinate(clampToMargins(getcoordinate("x"), "x"), "x"),
                relCoordinate(clampToMargins(getcoordinate("y"), "y"), "y")
            )
        );
    }
}


export class OwayoShaderSourceForSelObCreator extends ShaderPrinter {


    public static getShaderSource(index, druckBereich: BoxRelativeToPart | null, schwarzAufhellenImDruckbereich=false, nichtDruckbereich:BoxRelativeToPart|null): string[] {
        const p = OwayoShaderSourceForSelObCreator;
        const nthEntry = p.nthArrayEntry(index);

        const out =  [
            p.assign(
                "posInSelOb",
                p.times(
                    nthEntry('selObMatrices'),
                    p.vec3("PixelPositioninOriginal", "1.0")
                )
            ),
            p.if(p.isRepeatLogo(nthEntry))(
                p.if(p.shouldBeShownOnCurrentColor(nthEntry))(
                    // p.getInmarginSpace(nthEntry),
                    p.assignColorTmp(LogoRepeatColorGetter.getLogoRepeatColorResult(nthEntry)),
                    p.mixColorResultWithColorTmp()
                )
            ),
            p.else(
                p.if(p.isWithinMargins(nthEntry))(
                    p.if(p.shouldBeShownOnCurrentColor(nthEntry))(
                        // OwayoShaderSourceForSelObCreator.getWasserzeichenCode(index),
                        p.assignColorTmp(p.getLogoColorResult(nthEntry)),
                        schwarzAufhellenImDruckbereich ? p.brightenBlackInColorTmp(CONSTANTS.LOGO_SCHWARZ_AUFHELLEN_TARGET_DECIMALVALUE):"",
                        p.mixColorResultWithColorTmp()
                    )
                )
            )
        ];

        if (druckBereich === null) {
            return out;
        }
        return [
            p.if(OwayoShaderDruckbereichPrinter.isInDruckbereich(druckBereich, nichtDruckbereich))(out.join("\n")),
            p.else(OwayoShaderDruckbereichPrinter.greyOutNotPrintableAreaIfFlagIsSet())
        ];
    }

    private static assignColorTmp(tmpColor) {
        return OwayoShaderSourceForSelObCreator.assign("ColorTmp", tmpColor);
    }

    private static brightenBlackInColorTmp(targetValue) {
        const val = targetValue.toFixed(2);
        const p = ShaderPrinter;
        return p.if(
            p.allComponentsLessThan("ColorTmp", ["r", "g", "b"], val)(
                p.assign("ColorTmp.rgb", p.vec3(val))
            )
        )
    }

    private static mixColorResultWithColorTmp() {
        return OwayoShaderSourceForSelObCreator.assign(
            "ColorResult",
            "vec4(mix(ColorResult.rgb,ColorTmp.rgb,ColorTmp.a),ColorResult.a+ColorTmp.a)"
        );
    }

    private static isRepeatLogo(nthEntry) {
        return OwayoShaderSourceForSelObCreator.notEqual(nthEntry("selObRepeat"), "0");
    }

    private static getLogoColorResult(nthIndex: (arrayname) => string): string {
        function getRelCoordinate(dimension) {
            const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
            return ShaderPrinter.divide(dim("posInSelOb"), dim(nthIndex("SelectableObjectTextureSize")));
        }

        return ShaderPrinter.texture2D(nthIndex("SelectableObjectTexture"), ShaderPrinter.vec2(getRelCoordinate("x"), getRelCoordinate("y")));
    }

    // private static getLogoRepeatColorResult(nthIndex: (arrayname) => string): string {
    //     const p = OwayoShaderSourceForSelObCreator;
    //
    //     function getcoordinate(dimension) {
    //         const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
    //         return p.add(
    //             p.mod(
    //                 p.subtract(dim("posInSelOb"), dim(nthIndex("selObMargins"))),
    //                 p.subtract(
    //                     dim(nthIndex("SelectableObjectTextureSize")),
    //                     p.times("2.0", dim(nthIndex("selObMargins")))
    //                 )
    //             ),
    //             dim(nthIndex("selObMargins"))
    //         );
    //     }
    //
    //     function relCoordinate(coord, dimension): string {
    //         const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
    //         return p.divide(coord, dim(nthIndex("SelectableObjectTextureSize")));
    //     }
    //
    //     function clampToMargins(val, dimension): string {
    //         const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);
    //         const minMargin = ShaderPrinter.roundbracket(ShaderPrinter.add(dim(nthIndex("selObMargins")), "2.0"));
    //         const maxMargin = p.subtract(dim(nthIndex("SelectableObjectTextureSize")), minMargin);
    //         return p.clamp(val, minMargin, maxMargin);
    //     }
    //
    //     return p.texture2D(
    //         nthIndex('SelectableObjectTexture'),
    //         p.vec2(
    //             relCoordinate(clampToMargins(getcoordinate("x"), "x"), "x"),
    //             relCoordinate(clampToMargins(getcoordinate("y"), "y"), "y")
    //         )
    //     );
    // }

    private static shouldBeShownOnCurrentColor(nthIndex: (arrayname) => string): string {
        return ShaderPrinter.or(
            ShaderPrinter.compare(nthIndex('selObClampToColor'), "0", "<"),
            ShaderPrinter.equal(nthIndex("selObClampToColor"), " int(A+0.1)")
        );
    }

    private static getOutermargin(nthIndex, dimension) {
        const dim = OwayoShaderSourceForSelObCreator.getDimFunction(dimension);

        return ShaderPrinter.subtract(
            dim(nthIndex("SelectableObjectTextureSize")),
            dim(nthIndex("selObMargins"))
        );
    }

    private static isWithinMargins(nthIndex: (arrayname) => string): string {
        const p = OwayoShaderSourceForSelObCreator;
        return p.and(
            p.greaterOrEqualThan(p.x("posInSelOb"), p.x(nthIndex("selObMargins"))),
            p.greaterOrEqualThan(p.y("posInSelOb"), p.y(nthIndex("selObMargins"))),
            p.lessOrEqualThan(p.x("posInSelOb"), p.getOutermargin(nthIndex, "x")),
            p.lessOrEqualThan(p.y("posInSelOb"), p.getOutermargin(nthIndex, "y")),
        );
    }

    // private static getWasserzeichenCode(index) {
    //     const p = OwayoShaderSourceForSelObCreator;
    //     const nthEntry = p.nthArrayEntry(index);
    //     return [
    //         p.if(this.isNotWasserzeichen(nthEntry))(
    //             p.mixColorResultWithColorTmp()
    //         ),
    //         p.else(
    //             p.if(this.isNotInvertedWasserzeichen(nthEntry))(
    //                 p.assign("ColorTmp.rgb", p.subtract("1.0", "ColorTmp.rgb")),
    //             ),
    //             p.assign(
    //                 "ColorResult.rgb",
    //                 p.times(
    //                     "ColorResult.rgb",
    //                     p.add(
    //                         "1.0",
    //                         p.times(
    //                             p.add("0.229*ColorTmp.r", "0.587*ColorTmp.g", "0.114*ColorTmp.b"),
    //                             nthEntry("selObWasserzeichenIntensity")
    //                         )
    //                     )
    //                 )
    //             )
    //         )
    //     ];
    // }

    // private static isNotInvertedWasserzeichen(nthEntry) {
    //     return OwayoShaderSourceForSelObCreator.equal(nthEntry("selObWasserzeichenInvertImage"), "0");
    // }
    //
    // private static isNotWasserzeichen(nthEntry) {
    //     return OwayoShaderSourceForSelObCreator.equal(nthEntry("selObWasserzeichen"), "0");
    // }
}

export class HandleShaderCreatorPrinter extends ShaderPrinter {
    public static getHandleShaderSource(index) {
        const p = HandleShaderCreatorPrinter;
        const nthIndex = p.nthArrayEntry(index);

        return [
            p.assign(
                p.x("pixelPosInHandle"),
                p.times(
                    p.subtract(
                        p.x("pixelCoord"),
                        p.x(nthIndex("handleTranslation"))
                    ),
                    "inversePartStauchungsfaktor"
                )
            ),
            p.assign(
                p.y("pixelPosInHandle"),
                p.subtract(
                    p.y("pixelCoord"),
                    p.y(nthIndex("handleTranslation"))
                )
            ),
            p.assign("pixelPosInHandle", p.add("pixelPosInHandle", p.times("handleIconSize", "0.5"))),
            p.if(p.equal(p.clamp("pixelPosInHandle", p.vec2("0.0"), "handleIconSize"), "pixelPosInHandle"))(
                p.brace("+=", p.x("pixelPosInHandle"), p.times(nthIndex("handleIconIndex"), p.x("handleIconSize"))) + p.endline,
                p.assign(
                    "gl_FragColor",
                    p.texture2D(
                        p.nthArrayEntry(0, "handleTexture"),
                        p.divide("pixelPosInHandle", "handleTextureSize")
                    )
                ),
                p.assign("hitHandler", "true")
            )

        ].join("");
    }

    public static returnIfHandleHit():string {
        const self = HandleShaderCreatorPrinter;
        return self.if(self.equal("hitHandler", "true"))("return;\n");
    }
}
